/**
 * Route guard for protected modules per ADR-039.
 *
 * Three states matter:
 *   - loading              → render nothing (no flash-of-protected-content)
 *   - authenticated        → render children
 *   - unauthenticated      → bounce to Cognito hosted UI via login()
 *   - error (network/5xx)  → render the error message; do NOT bounce
 *                            (auto-redirect on a sick API just makes
 *                            things worse — let the user retry)
 */

import { useEffect, type ReactNode } from 'react';
import { useAuth } from './AuthContext.js';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, error, login, refresh } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      login();
    }
  }, [isAuthenticated, isLoading, error, login]);

  if (isLoading) return null;

  if (error) {
    return (
      <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>We couldn&rsquo;t reach the API</h2>
        <p>
          {error === 'network'
            ? 'Check your connection and try again.'
            : 'The server is temporarily unavailable. Please try again in a moment.'}
        </p>
        <button type="button" onClick={() => void refresh()}>
          Retry
        </button>
      </div>
    );
  }

  if (!isAuthenticated) return null;
  return <>{children}</>;
}
